import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui';
import {
  MISSING_FIELD_PRESETS,
  vendorResubmissionSchema,
  type VendorResubmissionValues,
} from '../schemas/vendorReviewSchema';

export interface VendorRequestResubmissionModalProps {
  isOpen: boolean;
  applicationName: string;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onSubmit: (values: VendorResubmissionValues) => void;
  onCancel: () => void;
}

export const VendorRequestResubmissionModal = ({
  isOpen,
  applicationName,
  isSubmitting,
  errorMessage,
  onSubmit,
  onCancel,
}: VendorRequestResubmissionModalProps) => {
  const { t } = useTranslation('vendors');
  const form = useForm<VendorResubmissionValues>({
    resolver: zodResolver(vendorResubmissionSchema),
    defaultValues: { reason: '', missingFields: [] },
    mode: 'onBlur',
  });
  const reasonRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      form.reset({ reason: '', missingFields: [] });
      requestAnimationFrame(() => reasonRef.current?.focus());
    }
  }, [isOpen, form]);

  const { ref: registerRef, ...reasonRegister } = form.register('reason');
  const reasonError = form.formState.errors.reason?.message;
  const missingFieldsError = form.formState.errors.missingFields?.message;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent data-testid="vendor-resubmit-modal" className="max-w-lg">
        <DialogTitle>{t('vendors.actions.requestResubmission')}</DialogTitle>
        <DialogDescription>
          {t('vendors.actionModal.subject', { vendorName: applicationName })}
        </DialogDescription>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="vendor-resubmission-reason"
              className="text-sm text-[color:var(--content-muted)]"
            >
              {t('vendors.actionModal.resubmissionReason')}
              <span aria-hidden="true" className="ml-1 text-[color:var(--signal-danger)]">
                *
              </span>
            </label>
            <textarea
              id="vendor-resubmission-reason"
              {...reasonRegister}
              ref={(el) => {
                registerRef(el);
                reasonRef.current = el;
              }}
              required
              aria-required="true"
              aria-invalid={Boolean(reasonError)}
              aria-describedby={reasonError ? 'vendor-resubmission-reason-error' : undefined}
              maxLength={1000}
              rows={3}
              className="w-full rounded-md border px-3 py-2 text-sm bg-[color:var(--surface-0)] text-[color:var(--content-strong)] placeholder:text-[color:var(--content-muted)] focus-visible:outline-none focus-visible:ring-2"
              style={{ borderColor: 'var(--content-muted)' }}
            />
            {reasonError ? (
              <span
                id="vendor-resubmission-reason-error"
                role="alert"
                className="text-xs"
                style={{ color: 'var(--signal-danger)' }}
              >
                {t(reasonError, { defaultValue: reasonError })}
              </span>
            ) : null}
          </div>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm text-[color:var(--content-muted)]">
              {t('vendors.actionModal.missingFields')}
              <span aria-hidden="true" className="ml-1 text-[color:var(--signal-danger)]">
                *
              </span>
            </legend>
            <Controller
              control={form.control}
              name="missingFields"
              render={({ field }) => {
                const selected = new Set(field.value);
                return (
                  <div role="group" aria-labelledby="vendor-resubmission-missing-fields-legend" className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                    {MISSING_FIELD_PRESETS.map((key) => (
                      <label
                        key={key}
                        className="inline-flex items-center gap-2 text-sm text-[color:var(--content-default)]"
                      >
                        <input
                          type="checkbox"
                          value={key}
                          checked={selected.has(key)}
                          onChange={(e) => {
                            const next = new Set(selected);
                            if (e.target.checked) next.add(key);
                            else next.delete(key);
                            field.onChange(Array.from(next));
                          }}
                        />
                        {t(`vendors.missingFields.${key}`, { defaultValue: key })}
                      </label>
                    ))}
                  </div>
                );
              }}
            />
            {missingFieldsError ? (
              <span
                role="alert"
                className="text-xs"
                style={{ color: 'var(--signal-danger)' }}
              >
                {t(missingFieldsError, { defaultValue: missingFieldsError })}
              </span>
            ) : null}
          </fieldset>

          {errorMessage ? (
            <p
              role="alert"
              aria-live="polite"
              className="rounded-md p-2 text-sm"
              style={{
                background: 'var(--status-danger-bg)',
                color: 'var(--status-danger-fg)',
              }}
            >
              {errorMessage}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t('vendors.actions.cancel')}
            </Button>
            <Button type="submit" variant="cta" size="md" disabled={isSubmitting}>
              {isSubmitting
                ? t('vendors.actionModal.submitting')
                : t('vendors.actions.resubmissionConfirm')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
